import { getConfig } from './config';
import { BrandCode } from './config/config';
import { fetchData } from './fetcher';

const DEFAULT_CRUISE_SEARCH_TIMEOUT = 20000;

interface GraphQLError {
	message: string;
	extensions: Record<string, unknown>;
}

export interface GraphQLHeaders {
	queryName: string;
	country?: string;
	currency?: string;
	language?: string;
	office?: string;
	sessionId?: string;
	countryAlpha2Code?: string;
	brand?: BrandCode;
}

interface GraphQLResponse<T> {
	errors?: GraphQLError[];
	data: T;
}

export interface GraphQLQuery {
	name: string;
	query: string;
}

// TODO: Add GraphQLData types for the filters query
export interface GraphQLQueryFiltersData {
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	cruiseSearch: any;
}

export interface GraphQLQueryLocaleData {
	locale: {
		path: string;
		currency: {
			code: string;
		};
		language: {
			code: string;
		};
		country: {
			code: string;
			isoAlpha2Code: string;
			office: {
				code: string;
			};
		};
	};
}

export class GraphQLClient {
	private getHeaders(headers: GraphQLHeaders, requestTimeout: number): Record<string, string | undefined> {
		const { queryName, country, currency, language, office, sessionId, brand, countryAlpha2Code } = headers;

		return {
			'Apollographql-Client-Name': 'cruise-search-widget',
			'Apollographql-Query-Name': queryName,
			'App-Name': 'graph',
			'Content-Type': 'application/json',
			country,
			currency,
			language,
			office,
			countryAlpha2Code,
			'x-session-id': sessionId,
			'request-timeout': (requestTimeout / 1000).toString(),
			brand,
		};
	}

	public async query<T>(
		query: GraphQLQuery,
		variables: Record<string, unknown>,
		headers: GraphQLHeaders,
		abortRequest?: AbortController,
	): Promise<T> {
		const config = getConfig();
		const controller = new AbortController();
		const requestTimeout = config.endpoints.cruiseSearchTimeout ?? DEFAULT_CRUISE_SEARCH_TIMEOUT;
		const timeoutId = setTimeout(() => controller.abort(), requestTimeout);

		abortRequest?.signal.addEventListener('abort', (reason) => controller.abort(reason));

		try {
			const response = await fetchData<GraphQLResponse<T>>(`${config.endpoints.cruiseSearchApi}`, {
				method: 'POST',
				body: JSON.stringify({ query: query.query, variables }),
				headers: this.getHeaders(headers, requestTimeout) as HeadersInit,
				signal: controller.signal,
			});

			if (response?.data?.data) {
				return response.data.data;
			}
		} catch (error) {
			console.error(error);
		} finally {
			clearTimeout(timeoutId);
		}

		return undefined as T;
	}
}
